import type { App } from '../../app/App';
import { Music } from '../../assets/Music';
import { Ease } from '../animation/Ease';
import type { Scene } from './Scene';
import { runTransition, type TransitionKind } from './Transitions';
import type { ViewportInfo } from './Viewport';
import { layoutStage } from '../../ui/stage';
import { preloadSkin, decorateSkinTitles } from '../../ui/skin';
import { rememberScene } from '../../app/resume';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyScene = Scene<any>;

export interface SceneRoutes {
  menu: void;
  notebook: { tab?: string } | void;
  battle: import('../scenes/BattleScene').BattleParams;
  creator: { monsterId?: string; heroMode?: boolean } | void;
  help: void;
  multiplayer: import('../scenes/MultiplayerScene').MultiplayerParams;
}
export type SceneName = keyof SceneRoutes;

type Factory = (app: App) => AnyScene;

/**
 * Owns the active scene and its lifecycle. Exactly one scene is alive at a
 * time except during a transition; the old one is destroyed (DOM, Pixi
 * objects, listeners) as soon as the new one is shown.
 */
export class SceneManager {
  private factories = new Map<SceneName, Factory>();
  current: AnyScene | null = null;
  currentName: SceneName | null = null;
  /** Params the open scene was entered with (for reopening it after a reload). */
  currentParams: unknown = null;
  private outgoing: AnyScene | null = null;
  private busy = false;
  private queued: (() => void) | null = null;
  private foreground: HTMLElement | null = null;
  /** Asked before every navigation; false keeps the current scene (it explains why itself). */
  guard: ((name: SceneName, params: unknown) => boolean) | null = null;

  constructor(
    private readonly app: App,
    private readonly uiLayer: HTMLElement,
    private readonly overlay: HTMLElement,
  ) {}

  register(name: SceneName, factory: Factory): void {
    this.factories.set(name, factory);
  }

  async go<N extends SceneName>(name: N, params: SceneRoutes[N], transition: TransitionKind = 'page'): Promise<void> {
    if (this.guard && !this.guard(name, params)) return;
    if (this.busy) {
      // Coalesce rapid navigation: only the most recent request wins.
      this.queued = () => void this.go(name, params, transition);
      return;
    }
    if (this.current && !(await this.current.canLeave())) return;
    const factory = this.factories.get(name);
    if (!factory) throw new Error(`unknown scene ${name}`);
    this.busy = true;
    // Building a scene and its transition stutter by design: not a reason to lower quality.
    this.app.quality?.pause(3000);
    try {
      await preloadSkin(name);
      const next = factory(this.app);
      await next.enter(params);
      decorateSkinTitles(next.dom);
      if (['menu','notebook','battle','creator'].includes(name)) layoutStage(next.dom);
      next.layout(this.app.viewport.info);
      this.showForeground(next.dom);

      const prev = this.current;
      this.outgoing = prev;
      this.current = next;
      this.currentName = name;
      this.currentParams = params;
      this.app.music.play(Music.forScene(name));
      this.app.applyFrameRate();
      rememberScene(name, params);
      if (location.hash !== `#/${name}` && name !== 'battle') history.replaceState(null, '', `#/${name}`);

      next.root.alpha = 0;
      this.app.pixi.stage.addChild(next.root);
      if (prev) void this.app.tweens.to(prev.root, { alpha: 0 }, 160);

      await runTransition(transition, this.uiLayer, this.overlay, prev?.dom ?? null, next.dom, () => {
        this.uiLayer.appendChild(next.dom);
      });

      if (prev) prev.destroy();
      this.outgoing = null;
      // Layout again now that the DOM is in its final position.
      next.layout(this.app.viewport.info);
      void this.app.tweens.to(next.root, { alpha: 1 }, 220, { ease: Ease.outQuad });
      next.shown();
    } catch (err) {
      console.error(`failed to open scene ${name}`, err);
      this.app.toast(`Oops! The page "${name}" got smudged. Please try again.`, 'error');
    } finally {
      this.busy = false;
      const q = this.queued;
      this.queued = null;
      q?.();
    }
  }

  update(dt: number): void {
    this.outgoing?.update(dt);
    this.current?.update(dt);
  }

  layout(vp: ViewportInfo): void {
    if (this.current && ['menu','notebook','battle','creator'].includes(this.currentName ?? '')) layoutStage(this.current.dom);
    this.current?.layout(vp);
    if (this.current && this.foreground) {
      layoutStage(this.foreground);
    }
  }

  /** Props sit above the transparent Pixi canvas, never intercepting controls. */
  private showForeground(dom: HTMLElement): void {
    this.foreground?.remove();
    this.foreground = null;
    const props = dom.querySelector<HTMLElement>('.desk-props');
    if (!props) return;
    const layer = document.createElement('div');
    layer.className = `${dom.className} skin-foreground`;
    layer.setAttribute('aria-hidden','true');
    layer.appendChild(props.cloneNode(true));
    props.classList.add('props-source');
    layoutStage(layer);
    document.body.appendChild(layer);
    this.foreground = layer;
  }
}
